import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";

/**
 * Shared helper for multi-tab Formik forms where validation errors can live on a
 * tab that isn't currently visible. Without this, clicking Save while an error
 * sits on a hidden tab makes nothing happen and the user has no idea why.
 *
 * It validates the whole form on Save, marks all fields touched (so inline
 * errors render), and if any tab has an error it switches to the first errored
 * tab (in `tabOrder`) and shows an alert naming that tab.
 *
 * @param {object}   params
 * @param {object}   params.formik          - the useFormik() instance
 * @param {object}   params.fieldToTab      - map of fieldName -> tab value
 * @param {string[]} params.tabOrder        - tab values in display order
 * @param {object}   params.tabLabels       - map of tab value -> human label
 * @param {Function} params.setSelectedTab  - setter to switch the active tab
 * @returns {{ handleSaveClick: Function, focusFirstErroredTab: Function }}
 */
export function useTabbedFormErrors({
  formik,
  fieldToTab,
  tabOrder,
  tabLabels,
  setSelectedTab,
}) {
  const dispatch = useDispatch();
  const { validateForm, setTouched, handleSubmit } = formik;

  // Builds a "touched" tree mirroring the shape of Formik's errors, so that
  // NESTED errored fields (e.g. billingInfo.pan, payoutAccounts[0].account) also
  // get marked touched and render their red inline error — not just top-level
  // fields. Arrays map to arrays, plain objects recurse, leaves become `true`.
  const buildTouchedTree = useCallback((errorNode) => {
    if (Array.isArray(errorNode)) {
      return errorNode.map((item) =>
        item && typeof item === "object" ? buildTouchedTree(item) : true
      );
    }
    if (errorNode && typeof errorNode === "object") {
      return Object.keys(errorNode).reduce((acc, key) => {
        acc[key] = buildTouchedTree(errorNode[key]);
        return acc;
      }, {});
    }
    return true;
  }, []);

  // Finds the first tab (in tabOrder) that contains an errored field, switches
  // to it, and alerts the user. Returns true if an errored tab was found.
  const focusFirstErroredTab = useCallback(
    (validationErrors) => {
      const erroredTabs = new Set();
      Object.keys(validationErrors || {}).forEach((field) => {
        const tab = fieldToTab[field];
        if (tab) {
          erroredTabs.add(tab);
        }
      });
      const firstErroredTab = (tabOrder || []).find((tab) =>
        erroredTabs.has(tab)
      );
      if (firstErroredTab) {
        setSelectedTab(firstErroredTab);
        dispatch(
          alertSlice.actions.setAlert({
            open: true,
            message: `Please fix the highlighted errors in the "${
              (tabLabels && tabLabels[firstErroredTab]) || firstErroredTab
            }" tab.`,
            severity: "error",
          })
        );
        return true;
      }
      return false;
    },
    [dispatch, fieldToTab, tabOrder, tabLabels, setSelectedTab]
  );

  // Validate everything before submitting; mark all errored fields touched so
  // their inline errors render, then jump to the first errored tab (if any)
  // instead of letting the Save click silently do nothing.
  const handleSaveClick = useCallback(async () => {
    const validationErrors = await validateForm();
    setTouched(buildTouchedTree(validationErrors), false);
    if (focusFirstErroredTab(validationErrors)) {
      return;
    }
    handleSubmit();
  }, [validateForm, setTouched, buildTouchedTree, focusFirstErroredTab, handleSubmit]);

  return { handleSaveClick, focusFirstErroredTab };
}
